import "./safetyRegister.css";
import SafetyCorrectiveActions, { actionCounts } from "@/components/SafetyCorrectiveActions";
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RegisterFeedback, PageHeader, StatusBadge, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Sparkles, Loader2, AlertTriangle, Camera, FileWarning, ChevronDown, MapPin, Briefcase, Trash2, Wrench, FileCheck2, ChevronRight, ClipboardList, Settings2, ShieldCheck, ShieldAlert, Truck, Search, Download, CheckCircle2, RotateCcw, ClipboardCheck, Shovel, Cable, ArrowUp, Zap, FlaskConical, Leaf, Hand, HardHat, Footprints, Users, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { useSafetyNavigate as useNavigate, SafetyLink as Link } from "@/lib/safetyNavigation";
import { PhotoGrid } from "@/components/AuthImage";
import { uploadMany } from "@/lib/imageUpload";
import { UploadingThumb } from "@/components/UploadingThumb";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Switch } from "@/components/ui/switch";

const TYPE_LABEL = {
  hazard: "Hazard",
  incident: "Incident",
  near_miss: "Near Miss",
  vehicle_damage: "Vehicle Damage",
  toolbox: "Toolbox Talk",
  jsa: "JSA / SWMS",
};
const CATEGORY_ICONS = { plant_machinery: Wrench, vehicles_traffic: Truck, excavation_trenching: Shovel, underground_services: Cable, working_at_height: ArrowUp, electrical: Zap, hazardous_substances: FlaskConical, environmental: Leaf, manual_handling: Hand, ppe: HardHat, housekeeping_access: Footprints, public_third_party: Users, other: MoreHorizontal };
const TYPE_META = {
  hazard: { c: "#F59E0B", icon: AlertTriangle, hint: "Spotted something unsafe" },
  incident: { c: "#EF4444", icon: FileWarning, hint: "Someone got hurt / close call with harm" },
  near_miss: { c: "#8B5CF6", icon: ShieldAlert, hint: "Nearly went wrong — no harm done" },
  vehicle_damage: { c: "#3B82F6", icon: Truck, hint: "Damage to a truck, ute or machine" },
  toolbox: { c: "#22C55E", icon: ClipboardList, hint: "Record of a toolbox talk" },
  jsa: { c: "#FF5F15", icon: FileCheck2, hint: "Job safety analysis" },
};

function FormVisibilityDialog({ types, onClose, onSaved }) {
  const [vals, setVals] = useState(() =>
    Object.fromEntries(Object.keys(TYPE_LABEL).map((k) => [k, types[k] !== false])));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!Object.values(vals).some(Boolean)) return toast.error("Keep at least one form type on");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/safety-form-types", { types: vals });
      toast.success("Form visibility updated");
      onSaved(data.types);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="safety-visibility-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Form visibility</DialogTitle>
          <DialogDescription className="text-xs">
            Choose which safety forms your field crew can see and fill in. Office staff always see everything.
            Custom safety templates are toggled on the Forms page.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {Object.entries(TYPE_LABEL).filter(([k]) => k !== "toolbox").map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border border-zinc-300 p-3">
              <div className="font-bold text-sm uppercase tracking-wide">{v}</div>
              <Switch checked={!!vals[k]} onCheckedChange={(c) => setVals({ ...vals, [k]: c })}
                      data-testid={`safety-vis-toggle-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving}
                  className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase tracking-wider font-bold text-xs"
                  data-testid="safety-vis-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Safety() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const isOffice = ["admin", "office", "super_admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(() => params.get("type") || "");
  const [notifiableOnly, setNotifiableOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState(() => params.get("job_id") || "");
  const [withActions, setWithActions] = useState(() => params.get("actions") === "open");
  const [busyId, setBusyId] = useState(null);
  useEffect(() => { setWithActions(params.get("actions") === "open"); }, [params]);
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    const id = params.get("record");
    if (id) setExpanded(current => ({ ...current, [id]: true }));
  }, [params]);
  const [lightbox, setLightbox] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [enabledTypes, setEnabledTypes] = useState({});
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [visOpen, setVisOpen] = useState(false);
  const [safetyTemplates, setSafetyTemplates] = useState([]);
  const [q, setQ] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [sort, setSort] = useState("priority");
  const [fleet, setFleet] = useState([]);
  const [initialType, setInitialType] = useState(null);
  const [reportSession, setReportSession] = useState(0);
  const [reportConfig, setReportConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const loadConfig = async () => { setConfigError(false); try { const r = await api.get("/safety/reporting-options"); setReportConfig(r.data); } catch { setConfigError(true); } };
  useEffect(() => { loadConfig(); }, []);
  const nav = useNavigate();

  const isAdminish = user.role === "admin" || user.role === "office" || user.role === "super_admin";
  // Field workers only see enabled types; office always sees everything.
  // Missing keys (new types) default to enabled.
  // "toolbox" is legacy-display-only: toolbox talks live in their own hub now
  const visibleTypes = Object.keys(TYPE_LABEL).filter((k) => k !== "toolbox" && (isOffice || enabledTypes[k] !== false));

  useEffect(() => {
    const report = params.get("report");
    if (!typesLoaded || !["hazard", "incident", "near_miss", "jsa"].includes(report)) return;
    const next = new URLSearchParams(params);
    next.delete("report");
    setParams(next, { replace: true });
    if (!isOffice && enabledTypes[report] === false) return;
    setInitialType(report);
    setReportSession(n => n + 1);
    setOpen(true);
  }, [params, setParams, typesLoaded, enabledTypes, isOffice]);

  const load = async () => {
    setLoadError(false);
    setLoaded(false);
    try {
      const { data } = await api.get("/safety", { params: { with_open_actions: withActions || undefined, status: statusFilter === "all" ? undefined : statusFilter, job_id: jobFilter || undefined } });
      const recordId = params.get("record");
      if (recordId && !data.some(row => row.id === recordId)) {
        try { const result = await api.get(`/safety/${encodeURIComponent(recordId)}`); data.unshift(result.data); }
        catch { toast.error("The linked record is unavailable or you do not have access."); }
      }
      setItems(data);
    } catch (error) {
      setLoadError(true);
      console.warn("Register request failed", { status: error.response?.status });
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, [withActions, statusFilter, jobFilter, params]);
  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => toast.error("Jobs could not be loaded. You can still report a specific location."));
    api.get("/fleet").then((r) => setFleet(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/settings/safety-form-types").then((r) => { setEnabledTypes(r.data.types || {}); setTypesLoaded(true); }).catch(() => {});
    api.get("/form_templates")
      .then((r) => setSafetyTemplates((r.data || []).filter((t) => t.category === "safety" && t.active)))
      .catch(() => {});
  }, []);

  const summarize = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/safety/${id}/summarize`);
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, ai_summary: data.ai_summary } : i));
      toast.success("AI summary generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI summary failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this safety record?")) return;
    await api.delete(`/safety/${id}`);
    toast.success("Removed");
    load();
  };

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const downloadPdf = async (s) => {
    try {
      const res = await api.get(`/safety/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${s.form_type || "safety"}-${s.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const ql = q.trim().toLowerCase();
  const filteredItems = items.filter((s) =>
    (!filter || s.form_type === filter) &&
    (severityFilter === "all" || (severityFilter === "urgent" ? ["high", "critical"].includes(s.severity) && s.status !== "closed" : s.severity === severityFilter)) &&
    (statusFilter === "all" || (s.status || "open") === statusFilter) &&
    (!jobFilter || s.job_id === jobFilter) &&
    (!withActions || actionCounts(s.corrective_action_items).open > 0) &&
    (!notifiableOnly || s.notifiable) &&
    (!ql || [s.description, s.location, s.job_title, s.user_name, s.vehicle_rego, TYPE_LABEL[s.form_type]]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql)))).sort((a, b) => {
      const newest = (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0);
      if (sort === "newest") return newest;
      if (sort === "oldest") return -newest;
      const rank = { critical: 4, high: 3, medium: 2, low: 1 };
      return Number(a.status === "closed") - Number(b.status === "closed") || (rank[b.severity] || 0) - (rank[a.severity] || 0) || newest;
    });
  const countBy = items.reduce((m, s) => { m[s.form_type] = (m[s.form_type] || 0) + 1; return m; }, {});
  const quickReport = (t) => { setInitialType(t); setReportSession(n => n + 1); setOpen(true); };

  const resetFilters = () => { setQ(""); setFilter(""); setStatusFilter("all"); setJobFilter(""); setWithActions(false); setNotifiableOnly(false); setSeverityFilter("all"); };
  const hasFilters = q || filter || statusFilter !== "all" || jobFilter || withActions || notifiableOnly || severityFilter !== "all";
  const summary = [
    { label: "Reports in view", value: items.length, hint: "Current status, job & action scope", icon: ClipboardList, tone: "neutral" },
    { label: "High priority", value: items.filter(s => s.status !== "closed" && ["high", "critical"].includes(s.severity)).length, hint: "High or critical · not closed", icon: AlertTriangle, tone: "urgent" },
    { label: "Open actions", value: items.reduce((n, s) => n + actionCounts(s.corrective_action_items).open, 0), hint: "Assigned work still to complete", icon: ClipboardCheck, tone: "amber" },
    { label: "Awaiting verification", value: items.reduce((n, s) => n + actionCounts(s.corrective_action_items).completed, 0), hint: "Completed actions to check", icon: ShieldCheck, tone: "green" },
  ];

  return (
    <div data-testid="safety-page" className="hazard-register px-4 py-4 md:p-6 lg:p-8 md:max-w-7xl md:mx-auto pb-24 md:pb-10">
      <PageHeader
        tag="Health & safety / Register"
        title="Hazards & incidents"
        subtitle={isOffice ? "See what needs attention, track controls and keep every report in one place." : "Report a hazard or incident, then track the reports and actions below."}
        action={
          <div className="flex items-center gap-2">
            {isAdminish && (
              <Button variant="outline" onClick={() => setVisOpen(true)}
                      className="rounded-sm border-2 border-zinc-800 uppercase tracking-wider font-bold text-xs"
                      data-testid="safety-visibility-button">
                <Settings2 className="w-4 h-4 mr-1.5" /> Visibility
              </Button>
            )}
            <Dialog open={open} onOpenChange={(v) => { if (v) setReportSession(n => n + 1); setOpen(v); if (!v) setInitialType(null); }}>
              <DialogTrigger asChild>
                <Button disabled={!visibleTypes.length} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="new-safety-button">
                  <Plus className="w-4 h-4 mr-2" /> New report
                </Button>
              </DialogTrigger>
              <NewSafetyForm initialJobId={params.get("job_id") || ""} config={reportConfig} configError={configError} loadConfig={loadConfig} key={`${reportSession}-${initialType || "any"}`} initialType={initialType} fleet={fleet} jobs={jobs} allowedTypes={visibleTypes} onDone={() => { setOpen(false); setInitialType(null); load(); }} />
            </Dialog>
          </div>
        }
      />

      <div className="hazard-summary" aria-label="Report overview" data-testid="safety-summary">
        {summary.map(({ label, value, hint, icon: Icon, tone }) => <div key={label} className={`hazard-stat hazard-stat--${tone}`}>
          <div className="hazard-stat-top"><span>{label}</span><Icon size={18} /></div>
          <strong>{!loaded || loadError ? "—" : value}</strong><p>{hint}</p>
        </div>)}
      </div>
      <h2 className="text-lg font-bold mb-1">Report something</h2>
      <p className="text-sm text-zinc-500 mb-3">Choose a report type to get started.</p>
      {/* One-tap quick report — pick what happened, form opens pre-filled */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-5" data-testid="safety-quick-report">
        {visibleTypes.map((k) => {
          const M = TYPE_META[k];
          return (
            <button key={k} type="button" onClick={() => quickReport(k)}
                    className="text-left rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#101620] p-3 hover:shadow-md hover:-translate-y-[1px] transition-all"
                    style={{ borderBottom: `3px solid ${M.c}` }}
                    data-testid={`quick-report-${k}`}>
              <span className="w-8 h-8 rounded-lg flex items-center justify-center mb-2"
                    style={{ backgroundColor: `${M.c}1C`, color: M.c }}>
                <M.icon className="w-4 h-4" strokeWidth={2.2} />
              </span>
              <div className="text-[12px] font-bold leading-tight">{TYPE_LABEL[k]}</div>
              <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">{M.hint}</div>
            </button>
          );
        })}
      </div>

      <section className="hazard-filters rounded-xl border border-zinc-200 dark:border-white/15 p-4 mb-4" aria-label="Find safety reports">
      <div className="hazard-register-heading"><div><h2 className="text-lg font-bold">Report register</h2><p className="text-sm text-zinc-500">Find a report, review the details and follow up.</p></div>
        <label className="hazard-sort">Sort by <select aria-label="Sort safety reports" value={sort} onChange={e => setSort(e.target.value)}><option value="priority">Priority first</option><option value="newest">Newest first</option><option value="oldest">Oldest first</option></select></label>
      </div>
      {/* Toolbar: search + type filters + notifiable */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="safety-toolbar">
        <div className="relative flex-1 min-w-[190px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 aria-label="Search safety reports" placeholder="Search reports, jobs or locations…"
                 className="pl-9 w-full rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]"
                 data-testid="safety-search-input" />
        </div>
        <button aria-pressed={filter === ""} onClick={() => setFilter("")}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2.5 min-h-11 text-xs font-bold uppercase tracking-wider transition-colors ${filter === "" ? "bg-[#111827] text-white border-transparent dark:bg-[#FF5F15]" : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300 hover:border-zinc-500"}`}
                data-testid="safety-filter-all">
          All <span className="font-mono">{items.length}</span>
        </button>
        {visibleTypes.map((k) => (
          <button key={k} aria-pressed={filter === k} onClick={() => setFilter(filter === k ? "" : k)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2.5 min-h-11 text-xs font-bold uppercase tracking-wider transition-colors ${filter === k ? "bg-[#111827] text-white border-transparent dark:bg-[#FF5F15]" : "border-zinc-300 dark:border-white/15 text-zinc-600 dark:text-zinc-300 hover:border-zinc-500"}`}
                  data-testid={`safety-filter-${k}`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: TYPE_META[k].c }} />
            {TYPE_LABEL[k]} <span className="font-mono">{countBy[k] || 0}</span>
          </button>
        ))}
        <div className="ml-auto">
          <Button size="sm"
            variant={notifiableOnly ? "default" : "outline"}
            className={`rounded-full uppercase font-bold ${notifiableOnly ? "bg-red-600 text-white hover:bg-red-700" : "border-red-600 text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"}`}
            onClick={() => setNotifiableOnly((v) => !v)}
            data-testid="safety-filter-notifiable"
          >
            <FileWarning className="w-3.5 h-3.5 mr-1.5" /> WorkSafe notifiable
          </Button>
        </div>
      </div>

      <div className="hazard-secondary-filters flex flex-wrap gap-2 mb-3 items-center text-xs">
        <label>Severity <select aria-label="Safety severity" value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}><option value="all">All severities</option><option value="urgent">High priority · active</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></label>
        <label>Status <select aria-label="Safety status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="min-h-11 border rounded px-2 bg-white dark:bg-[#101620] dark:text-white"><option value="all">All statuses</option><option value="open">Open</option><option value="in_review">In review</option><option value="closed">Closed</option></select></label>
        <label>Job <select aria-label="Safety job" value={jobFilter} onChange={e => setJobFilter(e.target.value)} className="min-h-11 max-w-[240px] border rounded px-2 bg-white dark:bg-[#101620] dark:text-white"><option value="">All jobs</option>{jobs.map(job => <option key={job.id} value={job.id}>{job.job_number ? `${job.job_number} · ` : ""}{job.title}</option>)}</select></label>
        <label className="inline-flex min-h-11 items-center gap-2"><input type="checkbox" checked={withActions} onChange={e => setWithActions(e.target.checked)} />With open actions</label>
      </div>
      {hasFilters && <Button variant="ghost" onClick={resetFilters} data-testid="safety-clear-filters"><RotateCcw className="w-3.5 h-3.5 mr-2" />Clear filters</Button>}
      </section>
      {loaded && !loadError && <p role="status" className="text-sm text-zinc-500 mb-3">{filteredItems.length} {filteredItems.length === 1 ? "report" : "reports"} shown · Expand a report for details and follow-up actions.</p>}
      {loadError ? <RegisterFeedback error onRetry={load} label="safety reports" /> : !loaded ? (
        <div className="space-y-3" data-testid="safety-loading">
          {[0, 1, 2].map((i) => (<div key={i} className="h-24 border-2 border-zinc-200 bg-zinc-50 animate-pulse" />))}
        </div>
      ) : filteredItems.length === 0 ? (
        <EmptyState title="No safety reports found"
                    description={!hasFilters && items.length === 0 ? "Spot a hazard? Tap a report tile above. Photos are encouraged." : "Try adjusting your search or filters."} />
      ) : (
        <div className="space-y-2" data-testid="safety-list">
          {filteredItems.map((s) => {
            const isOpen = !!expanded[s.id];
            const M = TYPE_META[s.form_type] || TYPE_META.hazard;
            return (
              <div key={s.id} className="hazard-record hard-card overflow-hidden" data-severity={s.severity || "low"} data-closed={s.status === "closed"} data-testid={`safety-card-${s.id}`}>
                <button
                  type="button"
                  onClick={() => toggle(s.id)}
                  aria-expanded={isOpen}
                  aria-controls={`safety-detail-${s.id}`}
                  className="w-full text-left px-4 py-4 hover:bg-zinc-50 dark:hover:bg-white/[0.03] transition-colors"
                  data-testid={`safety-toggle-${s.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                          style={{ backgroundColor: `${M.c}1C`, color: M.c }}>
                      <M.icon className="w-4 h-4" strokeWidth={2.2} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold text-[13.5px] uppercase tracking-tight" style={{ color: M.c }}>
                          {TYPE_LABEL[s.form_type] || s.form_type}
                        </span>
                        <span>{s.initial_risk ? <RiskBadge risk={s.initial_risk} /> : <StatusBadge status={s.severity || "low"} />}</span>
                        <span className="hazard-status text-[10px] font-bold uppercase">{(s.hazard_status || s.status || "open").replaceAll("_", " ")}</span>
                        {!!s.corrective_action_items?.length && <span className="text-[10px] text-zinc-500">{actionCounts(s.corrective_action_items).open} open actions · {actionCounts(s.corrective_action_items).completed} awaiting verification · {actionCounts(s.corrective_action_items).verified} verified</span>}
                        {s.vehicle_rego && (
                          <span className="inline-flex items-center gap-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-[10px] font-bold font-mono uppercase tracking-wider px-2 py-0.5 rounded-full">
                            <Truck className="w-3 h-3" /> {s.vehicle_rego}
                          </span>
                        )}
                        {s.notifiable && (
                          <span className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                            <FileWarning className="w-3 h-3" /> Potential notifiable event
                          </span>
                        )}
                      </div>
                      <p className={`mt-1 text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed ${isOpen ? "" : "line-clamp-2"}`}>{s.description}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-zinc-500">
                        <span className="font-mono">{new Date(s.created_at).toLocaleString("en-NZ")}</span>
                        <span>{s.user_name}</span>
                        {s.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{s.location}</span>}
                        {s.job_title && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{s.job_title}</span>}
                        {(s.photo_paths?.length || 0) > 0 && (
                          <span className="inline-flex items-center gap-1"><Camera className="w-3 h-3" />{s.photo_paths.length}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {s.severity === "critical" && <AlertTriangle className="w-5 h-5 text-red-600" />}
                      <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 border-t border-zinc-200 pt-4 space-y-4" id={`safety-detail-${s.id}`} data-testid={`safety-detail-${s.id}`}>
                    {s.job_id && <Link to={`/app/jobs/${encodeURIComponent(s.job_id)}/safety`} className="inline-flex min-h-11 items-center text-sm font-semibold text-[#FF5F15]">Open linked job: {s.job_title || "Job"} →</Link>}
                    <DetailGrid>
                      <DetailItem label="Location" value={s.location} />
                      <DetailItem label="Job" value={s.job_title} />
                      <DetailItem label={s.initial_risk ? "Risk priority (legacy mapping)" : "Severity"} value={s.initial_risk ? null : s.severity} />
                      <DetailItem label="Vehicle" value={s.vehicle_rego} mono />
                      <DetailItem label="Submitted" value={new Date(s.created_at).toLocaleString("en-NZ")} mono />
                    </DetailGrid>

                    <ReportDetails record={s} config={reportConfig} />
                    {s.actions_taken && (
                      <div>
                        <div className="label-tag mb-1.5">Actions taken</div>
                        <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{s.actions_taken}</p>
                      </div>
                    )}

                    {s.photo_paths?.length > 0 && (
                      <div>
                        <div className="label-tag mb-2">Photos ({s.photo_paths.length})</div>
                        <PhotoGrid paths={s.photo_paths} onClick={(p) => setLightbox(p)} />
                      </div>
                    )}

                    {s.ai_summary && (
                      <div className="border-l-2 border-[#FF5F15] bg-orange-50 p-3 text-xs text-zinc-800">
                        <div className="label-tag !text-[#FF5F15] mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Summary</div>
                        <div className="whitespace-pre-wrap">{s.ai_summary}</div>
                      </div>
                    )}

                    <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-zinc-100">
                      {!s.ai_summary ? (
                        <Button size="sm" variant="outline" className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold text-xs"
                          onClick={() => summarize(s.id)} disabled={busyId === s.id} data-testid={`summarize-${s.id}`}>
                          {busyId === s.id ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Summarising</> : <><Sparkles className="w-3 h-3 mr-1" /> AI Summary</>}
                        </Button>
                      ) : <span />}
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="rounded-sm border-zinc-900 uppercase tracking-wider font-bold text-xs"
                          onClick={() => downloadPdf(s)} data-testid={`safety-pdf-${s.id}`}>
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                        {isOffice && !s.corrective_action_items?.length && (
                          <Button size="sm" variant="ghost" className="rounded-sm text-zinc-500 hover:text-red-600 uppercase tracking-wider font-bold text-xs" onClick={() => remove(s.id)} data-testid={`delete-safety-${s.id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        )}
                      </div>
                    </div>

                    <SafetyCorrectiveActions record={s} onPhoto={setLightbox} onChanged={patch => setItems(prev => prev.map(item => item.id === s.id ? { ...item, ...patch } : item))} />
                    {isOffice && (
                      <ReviewPanel config={reportConfig} configError={configError} loadConfig={loadConfig} record={s} onUpdated={(u) => setItems((prev) => prev.map((i) => i.id === u.id ? u : i))} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isOffice && <h2 className="text-lg font-bold mt-8 mb-3">Safety documents & checks</h2>}
      {!isOffice && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="safety-hub">
          <HubTile to="/app/prestarts" icon={Wrench} title="Pre-Start" desc="Daily vehicle check" testId="hub-prestart" />
          <HubTile to="/app/sssps" icon={FileCheck2} title="SSSPs" desc="Site safety plans" testId="hub-sssps" highlight />
          <HubTile to="/app/inspections" icon={ClipboardList} title="Site walk" desc="Daily / weekly inspection" testId="hub-inspections" />
          <HubTile to="/app/forms" icon={ClipboardList} title="Forms" desc="Custom office forms" testId="hub-forms" />
          <HubTile to="/app/my-safety" icon={ShieldCheck} title="My safety actions" desc="View your assigned follow-up work" testId="hub-my-safety" />
        </div>
      )}

      {!isOffice && safetyTemplates.length > 0 && (
        <div className="mb-6" data-testid="safety-custom-forms">
          <div className="label-tag mb-2 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Company safety forms
          </div>
          <div className="space-y-2">
            {safetyTemplates.map((t) => (
              <button key={t.id}
                      onClick={() => nav(`/app/forms?fill=${t.id}`)}
                      className="w-full text-left bg-white border border-zinc-300 p-3 flex items-center gap-3 hover:border-[#FF5F15] transition-colors"
                      data-testid={`safety-template-${t.id}`}>
                <div className="w-9 h-9 bg-[#09090B] text-white flex items-center justify-center shrink-0">
                  <ClipboardList className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{t.name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{t.fields?.length || 0} questions</div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#FF5F15]" />
              </button>
            ))}
          </div>
        </div>
      )}

      {visOpen && (
        <FormVisibilityDialog
          types={enabledTypes}
          onClose={() => setVisOpen(false)}
          onSaved={(t) => { setEnabledTypes(t); setVisOpen(false); }}
        />
      )}
      <PhotoLightbox path={lightbox} open={!!lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}

function ReviewPanel({ record, onUpdated, config, configError, loadConfig }) {
  const [f, setF] = useState({
    review_root_cause: record.review_root_cause || "",
    review_corrective_actions: record.review_corrective_actions || "",
    review_preventive_actions: record.review_preventive_actions || "",
    review_notes: record.review_notes || "",
    residual_likelihood: record.residual_likelihood || "",
    residual_consequence: record.residual_consequence || "",
    verification_notes: record.verification_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const status = record.status || "open";

  const patch = async (payload, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/safety/${record.id}/manage`, payload);
      onUpdated(data);
      setF(current => ({ ...current, residual_likelihood: data.residual_likelihood || "", residual_consequence: data.residual_consequence || "", verification_notes: data.verification_notes || "" }));
      if (okMsg) toast.success(okMsg);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const reviewPayload = () => {
    if (!!f.residual_likelihood !== !!f.residual_consequence) { toast.error("Select both residual likelihood and consequence"); return null; }
    return { ...f, residual_likelihood: f.residual_likelihood ? Number(f.residual_likelihood) : null, residual_consequence: f.residual_consequence ? Number(f.residual_consequence) : null, action_revision: record.action_revision || 0 };
  };
  const saveReview = () => { const payload = reviewPayload(); if (payload) patch(payload, "Review saved"); };
  const setStage = stage => { const payload = reviewPayload(); if (payload) patch({ ...payload, hazard_status: stage }, "Hazard follow-up updated"); };
  const closeOut = () => {
    const actions = record.corrective_action_items || [];
    const outstanding = actions.filter(a => a.status !== "verified").length;
    if (outstanding && !window.confirm(`${outstanding} corrective actions remain unverified (including open or completed work). Close this record anyway? Actions and evidence will remain available.`)) return;
    const payload = reviewPayload();
    if (payload) patch({ ...payload, status: "closed", acknowledge_outstanding_actions: outstanding > 0 }, "Safety record closed");
  };
  const reopen = () => patch({ status: "open", action_revision: record.action_revision || 0 }, "Reopened");

  return (
    <div className="rounded-lg border border-[#0B1F3A]/20 bg-slate-50 dark:bg-white/5 p-4" data-testid={`safety-review-${record.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-tag flex items-center gap-1.5 !text-[#0B1F3A] dark:!text-zinc-200"><ClipboardCheck className="w-3.5 h-3.5" /> Office follow-up</div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest border ${
          status === "closed" ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200"
            : status === "in_review" ? "border-sky-500 bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-200"
              : "border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-200"}`} data-testid={`safety-status-${record.id}`}>
          {status === "closed" ? <CheckCircle2 className="w-3 h-3" /> : null}{status.replace("_", " ")}
        </span>
      </div>

      {record.form_type === "hazard" && <section className="space-y-3 mb-4"><p className="text-sm font-semibold">Hazard status: {(record.hazard_status || (status === "closed" ? "closed" : "reported")).replaceAll("_", " ")}</p><p className="text-sm text-muted-foreground">Assignments and due dates are managed in Corrective actions above. A reporter’s “controlled” response still needs office review.</p>{status !== "closed" && <div className="flex flex-wrap gap-2">{[["action_required", "Further action required"], ["controlled", "Controls implemented"], ["verified", "Verify hazard"]].map(([value, label]) => <Button key={value} type="button" variant="outline" disabled={busy} onClick={() => setStage(value)}>{label}</Button>)}</div>}</section>}
      {["hazard", "near_miss"].includes(record.form_type) && <section className="space-y-3 mb-4"><h3 className="font-semibold text-sm">Residual risk after controls</h3><RiskAssessment config={config} prefix="residual" likelihood={f.residual_likelihood} consequence={f.residual_consequence} onChange={values => setF(current => ({ ...current, ...values }))} />{!config && <ConfigNotice error={configError} onRetry={loadConfig} />}<div><Label htmlFor={`verification-${record.id}`}>Verification — what was checked?</Label><Textarea id={`verification-${record.id}`} rows={2} value={f.verification_notes} onChange={e => setF(current => ({ ...current, verification_notes: e.target.value }))} placeholder="Confirm the controls and record the evidence reviewed…" /></div>{record.verified_at && <p className="text-sm text-muted-foreground">Verified by {record.verified_by_name} · {new Date(record.verified_at).toLocaleString("en-NZ")}</p>}</section>}
      <details className="mb-3"><summary className="font-semibold text-sm cursor-pointer min-h-11">Review / investigation notes</summary>
      <div className="grid gap-3">
        {[["review_root_cause", "Root cause", "What underlying cause led to this?"],
          ["review_corrective_actions", "Corrective action notes (existing)", "What was done to fix it now?"],
          ["review_preventive_actions", "Preventive actions", "What will stop it happening again?"],
          ["review_notes", "Management notes", "Any other review comments"]].map(([k, label, ph]) => (
          <div key={k}>
            <Label className="label-tag">{label}</Label>
            <Textarea rows={2} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                      placeholder={ph} className="mt-1 rounded-sm border-zinc-400"
                      data-testid={`safety-${k.replace("review_", "")}-${record.id}`} />
          </div>
        ))}
      </div>

      </details>
      {record.reviewed_by && (
        <p className="text-[11px] text-zinc-500 mt-2">Last reviewed by {record.reviewed_by}{record.reviewed_at ? ` · ${new Date(record.reviewed_at).toLocaleDateString("en-NZ")}` : ""}</p>
      )}
      {status === "closed" && record.closed_by && (
        <p className="text-[11px] text-emerald-700 font-bold mt-1">Closed out by {record.closed_by}{record.closed_at ? ` · ${new Date(record.closed_at).toLocaleDateString("en-NZ")}` : ""}</p>
      )}

      <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-zinc-200">
        {status === "open" && <Button size="sm" variant="outline" disabled={busy} onClick={() => patch({ status: "in_review", action_revision: record.action_revision || 0 }, "Review started")}>Start review</Button>}
        <Button size="sm" onClick={saveReview} disabled={busy}
                className="rounded-sm bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white uppercase tracking-wider font-bold text-xs"
                data-testid={`safety-save-review-${record.id}`}>Save review</Button>
        {status !== "closed" ? (
          <Button size="sm" onClick={closeOut} disabled={busy}
                  className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-wider font-bold text-xs"
                  data-testid={`safety-close-${record.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Close out
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={reopen} disabled={busy}
                  className="rounded-sm uppercase tracking-wider font-bold text-xs"
                  data-testid={`safety-reopen-${record.id}`}>
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reopen
          </Button>
        )}
      </div>
    </div>
  );
}

function DetailGrid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">{children}</div>;
}
function DetailItem({ label, value, mono }) {
  if (!value) return null;
  return (
    <div>
      <div className="label-tag mb-1">{label}</div>
      <div className={`${mono ? "font-mono text-xs" : ""} text-foreground`}>{value}</div>
    </div>
  );
}

function NewSafetyForm({ jobs, allowedTypes, onDone, initialType, initialJobId = "", fleet = [], config, configError, loadConfig }) {
  const [form, setForm] = useState({
    form_type: initialType || allowedTypes?.[0] || "hazard", severity: "medium", location: "", job_id: initialJobId,
    description: "", actions_taken: "", notifiable: false,
    hazard_category: "", initial_likelihood: "", initial_consequence: "", immediate_controls: [],
    further_action_required: null, follow_up_action: "", follow_up_assigned_to: "", follow_up_due_date: "",
    anyone_injured: "unknown", damage_description: "", safe_to_operate: "unknown",
    vehicle_id: "",
    // NZ HSWA incident fields (only used when form_type === "incident")
    event_type: "", event_datetime: "",
    person_harmed_name: "", person_harmed_role: "", person_harmed_age: "",
    body_parts: "", injury_type: "", treatment_given: "", lost_time_days: "",
    witnesses: "", contributing_factors: "", root_cause: "", corrective_actions: "",
    plant_substance_involved: "",
    worksafe_notified: false, worksafe_reference: "", worksafe_notified_at: "",
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]); // in-flight: {key,url,pct,error}
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onPhotoChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    const items = files.map((f, i) => ({ key: `${Date.now()}-${i}`, file: f, url: URL.createObjectURL(f), pct: 0 }));
    setUploads((u) => [...u, ...items]);
    await uploadMany(items, "safety", {
      onProgress: (key, pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x))),
      onDone: (key, res) => {
        setPhotos((p) => [...p, res.path]);
        setUploads((u) => {
          const it = u.find((x) => x.key === key);
          if (it?.url) URL.revokeObjectURL(it.url);
          return u.filter((x) => x.key !== key);
        });
      },
      onError: (key, err) => {
        const failed = items.find(item => item.key === key);
        if (failed?.url) URL.revokeObjectURL(failed.url);
        toast.error(err?.response?.data?.detail || "Upload failed");
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: true } : x)));
        setTimeout(() => setUploads((u) => u.filter((x) => x.key !== key)), 4000);
      },
    });
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy || uploading) return;
    if (!form.description.trim()) return toast.error("Describe what happened or the hazard you found.");
    if (["hazard", "near_miss"].includes(form.form_type) && (!form.location.trim() || !form.initial_likelihood || !form.initial_consequence || !config)) return toast.error("Record the location, likelihood and consequence.");
    if (form.form_type === "vehicle_damage" && !form.vehicle_id) return toast.error("Select the vehicle / machine.");
    setBusy(true);
    try {
      const job = jobs.find((j) => j.id === form.job_id);
      const vehicle = fleet.find((v) => v.id === form.vehicle_id);
      // Strip empty strings on optional/enum fields so the backend Pydantic
      // model doesn't reject them. Numeric + list fields coerced from text.
      const isIncident = form.form_type === "incident";
      const cleaned = { ...form };
      const emptyToNull = [
        "event_type", "event_datetime", "person_harmed_name", "person_harmed_role",
        "injury_type", "treatment_given", "witnesses", "contributing_factors",
        "root_cause", "corrective_actions", "plant_substance_involved",
        "worksafe_reference", "worksafe_notified_at",
      ];
      emptyToNull.forEach((k) => { if (cleaned[k] === "") cleaned[k] = null; });
      cleaned.person_harmed_age = cleaned.person_harmed_age === "" ? null : parseInt(cleaned.person_harmed_age, 10);
      cleaned.lost_time_days = cleaned.lost_time_days === "" ? null : parseFloat(cleaned.lost_time_days);
      cleaned.body_parts = (form.body_parts || "").trim()
        ? form.body_parts.split(",").map((s) => s.trim()).filter(Boolean)
        : null;
      // Wipe HSWA fields entirely if not an incident
      if (!isIncident) {
        emptyToNull.forEach((k) => { cleaned[k] = null; });
        cleaned.person_harmed_age = null;
        cleaned.lost_time_days = null;
        cleaned.body_parts = null;
        cleaned.worksafe_notified = false;
        cleaned.notifiable = false;
      }
      const assessed = ["hazard", "near_miss"].includes(form.form_type);
      Object.assign(cleaned, {
        initial_likelihood: assessed ? Number(form.initial_likelihood) : null,
        initial_consequence: assessed ? Number(form.initial_consequence) : null,
        hazard_category: assessed ? form.hazard_category || null : null,
        immediate_controls: assessed ? form.immediate_controls : [],
        anyone_injured: isIncident ? form.anyone_injured : null,
        damage_description: form.form_type === "vehicle_damage" ? form.damage_description : null,
        safe_to_operate: form.form_type === "vehicle_damage" ? form.safe_to_operate : null,
        further_action_required: form.form_type === "jsa" ? null : form.further_action_required,
        follow_up_action: form.form_type !== "jsa" && form.further_action_required ? form.follow_up_action || null : null,
        follow_up_assigned_to: form.form_type !== "jsa" && form.further_action_required ? form.follow_up_assigned_to || null : null,
        follow_up_due_date: form.form_type !== "jsa" && form.further_action_required ? form.follow_up_due_date || null : null,
      });
      await api.post("/safety", {
        ...cleaned,
        job_id: form.job_id || null,
        job_title: job?.title || null,
        vehicle_id: form.form_type === "vehicle_damage" ? (form.vehicle_id || null) : null,
        vehicle_rego: form.form_type === "vehicle_damage" ? (vehicle?.rego || null) : null,
        photo_paths: photos,
      });
      toast.success("Safety report submitted");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const isHazard = form.form_type === "hazard";
  const isNearMiss = form.form_type === "near_miss";
  const isIncident = form.form_type === "incident";
  const isVehicle = form.form_type === "vehicle_damage";
  const assessed = isHazard || isNearMiss;
  const context = {
    hazard: ["Report a hazard", "Capture the hazard, assess the risk and record what has been done to make the area safe.", "What did you find?", "Describe the hazard and what could happen if it is not controlled..."],
    incident: ["Report an incident", "Record what happened and the immediate care or action taken. Investigation can follow later.", "What happened?", "Describe what happened and who or what was affected..."],
    near_miss: ["Report a near miss", "Capture what almost happened, the potential harm and the controls put in place.", "What almost happened?", "Describe the near miss and what could have happened..."],
    vehicle_damage: ["Report vehicle / machine damage", "Identify the equipment, record the damage and let the office know whether it is safe to operate.", "How did it happen?", "Describe how the damage happened, or say if the cause is unknown..."],
    jsa: ["JSA / SWMS report", "Record the existing job safety analysis. This entry point remains available.", "Description", "Describe the task and safety analysis..."],
  }[form.form_type];
  const changeType = type => setForm(f => ({ ...f, form_type: type, notifiable: false, worksafe_notified: false }));
  return (
    <DialogContent className="hazard-report-dialog" hideClose={busy || uploading} onInteractOutside={e => { if (busy || uploading) e.preventDefault(); }} onEscapeKeyDown={e => { if (busy || uploading) e.preventDefault(); }}>
      <DialogHeader className="safety-report-header">
        <DialogTitle className="text-xl font-semibold">{context[0]}</DialogTitle>
        <DialogDescription>{context[1]}</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="safety-report-form">
        <fieldset disabled={busy} className="space-y-5 min-w-0">
        <div className="safety-type-options" role="group" aria-label="Report type" data-testid="safety-type-picker">
          {Object.keys(TYPE_LABEL).filter(k => k !== "toolbox" && (!allowedTypes || allowedTypes.includes(k))).map(k => { const M = TYPE_META[k]; return <button key={k} type="button" aria-pressed={form.form_type === k} onClick={() => changeType(k)} className="safety-type-option" data-testid={`safety-type-${k}`}><M.icon className="w-4 h-4 shrink-0" /><span>{TYPE_LABEL[k]}</span></button>; })}
        </div>
        <section className="space-y-3">
          <h3 className="safety-section-title">Where</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Job / site, if known</Label><Select value={form.job_id || "none"} onValueChange={v => setForm(f => ({ ...f, job_id: v === "none" ? "" : v }))}><SelectTrigger aria-label="Job / site" data-testid="safety-job-select"><SelectValue placeholder="Select job / site" /></SelectTrigger><SelectContent><SelectItem value="none">Not known / not job-specific</SelectItem>{jobs.map(j => <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} · ` : ""}{j.title}</SelectItem>)}</SelectContent></Select></div>
            <div><Label htmlFor="safety-location">{isVehicle ? "Where did it happen?" : "Specific location"}{assessed ? " *" : ""}</Label><Input id="safety-location" required={assessed} value={form.location} onChange={set("location")} placeholder="Chainage 450, excavation, workshop bay 2…" data-testid="safety-location-input" /></div>
          </div>
        </section>
        {isVehicle && <section className="space-y-3" data-testid="safety-vehicle-section"><h3 className="safety-section-title">Vehicle / machine</h3><div><Label>Which vehicle / machine? *</Label><Select value={form.vehicle_id} onValueChange={set("vehicle_id")} required><SelectTrigger aria-label="Which vehicle / machine" data-testid="safety-vehicle-select"><SelectValue placeholder="Select existing fleet record" /></SelectTrigger><SelectContent>{fleet.map(v => <SelectItem key={v.id} value={v.id}>{v.rego || "—"} · {[v.make, v.model].filter(Boolean).join(" ") || v.vehicle_type || "Vehicle"}</SelectItem>)}</SelectContent></Select></div><div><Label htmlFor="damage-description">What was damaged?</Label><Input id="damage-description" value={form.damage_description} onChange={set("damage_description")} placeholder="Panel, windscreen, hydraulic hose…" /></div><div><Label>Can the vehicle / machine still be safely operated?</Label><ChoiceOptions value={form.safe_to_operate} onChange={set("safe_to_operate")} label="Safe to operate" choices={[["yes", "Yes"], ["no", "No"], ["unknown", "Unsure — needs assessment"]]} /></div></section>}
        {assessed && <section className="space-y-2"><h3 className="safety-section-title">{isHazard ? "Hazard type" : "Near-miss category"}</h3>{config ? <Select value={form.hazard_category} onValueChange={set("hazard_category")}><SelectTrigger aria-label="Hazard category"><SelectValue placeholder="Select a category (optional)" /></SelectTrigger><SelectContent>{config.categories.map(([key, label]) => { const Icon = CATEGORY_ICONS[key] || AlertTriangle; return <SelectItem key={key} value={key}><span className="inline-flex items-center gap-2"><Icon className="w-4 h-4" />{label}</span></SelectItem>; })}</SelectContent></Select> : <ConfigNotice error={configError} onRetry={loadConfig} />}</section>}
        <section className="space-y-3">
          <div><Label htmlFor="safety-description" className="safety-section-title">{context[2]} *</Label><Textarea id="safety-description" required rows={3} value={form.description} onChange={set("description")} placeholder={context[3]} data-testid="safety-description-input" /></div>
                  <div>
          <Label className="label-tag">Photos ({photos.length})</Label>
          <div className="mt-2">
            <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-400 p-4 cursor-pointer hover:border-[#FF5F15] hover:text-[#FF5F15]" data-testid="safety-photo-upload">
              <Camera className="w-4 h-4" />
              <span className="text-sm font-semibold">{uploading ? "Uploading…" : "Add photos / evidence"}</span>
              <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={onPhotoChange} disabled={uploading || busy} />
            </label>
            {(photos.length > 0 || uploads.length > 0) && (
              <div className="mt-2 grid grid-cols-4 gap-2" data-testid="safety-photo-thumbs">
                {photos.map((p) => (
                  <div key={p} className="relative aspect-square border border-zinc-300 overflow-hidden bg-zinc-100">
                    <PhotoGrid paths={[p]} cols={1} />
                    <button type="button" aria-label="Remove photo" disabled={busy || uploading} onClick={() => setPhotos(current => current.filter(path => path !== p))} className="absolute top-0 right-0 min-h-11 min-w-11 bg-white text-red-700 rounded-bl-lg flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {uploads.map((u) => <UploadingThumb key={u.key} up={u} />)}
              </div>
            )}
          </div>
        </div>

        </section>
        {assessed ? <section className="space-y-2"><h3 className="safety-section-title">Initial risk assessment</h3><p className="text-sm text-muted-foreground">Consider the potential harm before the immediate controls.</p><RiskAssessment config={config} likelihood={form.initial_likelihood} consequence={form.initial_consequence} onChange={patch => setForm(f => ({ ...f, ...patch }))} prefix="initial" required />{!config && <ConfigNotice error={configError} onRetry={loadConfig} />}</section> : <div><Label>Report priority</Label><Select value={form.severity} onValueChange={set("severity")}><SelectTrigger aria-label="Report priority" data-testid="safety-severity-select"><SelectValue /></SelectTrigger><SelectContent>{["low", "medium", "high", "critical"].map(v => <SelectItem value={v} key={v}>{v[0].toUpperCase() + v.slice(1)}</SelectItem>)}</SelectContent></Select></div>}
        {isIncident && <section className="space-y-3"><h3 className="safety-section-title">People & immediate care</h3><div className="grid sm:grid-cols-2 gap-3"><div><Label htmlFor="event-when">When did it happen? (optional)</Label><Input id="event-when" type="datetime-local" value={form.event_datetime} onChange={set("event_datetime")} /><p className="text-xs text-muted-foreground mt-1">The reported date/time is recorded automatically.</p></div><div><Label>Anyone injured?</Label><ChoiceOptions value={form.anyone_injured} onChange={set("anyone_injured")} label="Anyone injured" choices={[["yes", "Yes"], ["no", "No"], ["unknown", "Unknown"]]} /></div></div>{form.anyone_injured !== "no" && <div><Label>Immediate treatment (if known)</Label><Select value={form.treatment_given} onValueChange={set("treatment_given")}><SelectTrigger aria-label="Immediate treatment"><SelectValue placeholder="Select treatment" /></SelectTrigger><SelectContent>{[["none", "None"], ["first_aid", "On-site first aid"], ["gp", "GP visit"], ["ed_hospital", "ED / hospital (not admitted)"], ["hospitalised", "Hospitalised"], ["ambulance", "Ambulance attended"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>}</section>}
        <section className="space-y-3"><h3 className="safety-section-title">{assessed ? "What have you done now?" : "Immediate action"}</h3>
          {assessed && config && <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">{config.controls.map(([key, label]) => <label key={key} className="flex items-center gap-2 min-h-11 text-sm cursor-pointer"><Checkbox checked={form.immediate_controls.includes(key)} onCheckedChange={checked => setForm(f => ({ ...f, immediate_controls: checked ? [...f.immediate_controls, key] : f.immediate_controls.filter(c => c !== key) }))} />{label}</label>)}</div>}
          <div><Label htmlFor="safety-actions">{assessed ? "Control / action details" : "Treatment / action details"} (optional)</Label><Textarea id="safety-actions" rows={2} value={form.actions_taken} onChange={set("actions_taken")} placeholder={assessed ? "Describe what was done to make the area safe..." : "Describe the immediate response..."} data-testid="safety-actions-input" /></div>
        </section>
        {form.form_type !== "jsa" && <section className="space-y-3"><h3 className="safety-section-title">Does anything else need to be done?</h3><ChoiceOptions label="Further action required" value={form.further_action_required == null ? "" : String(form.further_action_required)} onChange={v => setForm(f => ({ ...f, further_action_required: v === "true" }))} choices={[["false", isHazard ? "No — hazard controlled" : "No — immediate response complete"], ["true", "Yes — further action required"]]} />
          {form.further_action_required === false && <p className="text-sm text-muted-foreground">Submitted for office review. This does not verify or close the report.</p>}
          {form.further_action_required === true && <div className="space-y-3 border-l-2 border-[#FF5F15] pl-3"><div><Label htmlFor="follow-up-action">Corrective action (if known)</Label><Input id="follow-up-action" maxLength={200} value={form.follow_up_action} onChange={set("follow_up_action")} placeholder="What needs to happen next?" /></div><div className="grid sm:grid-cols-2 gap-3"><div><Label>{config?.can_assign ? "Assign to" : "Suggest a person"} (optional)</Label><Select value={form.follow_up_assigned_to || "none"} onValueChange={v => setForm(f => ({ ...f, follow_up_assigned_to: v === "none" ? "" : v }))}><SelectTrigger aria-label="Follow-up person"><SelectValue placeholder="Office to assign" /></SelectTrigger><SelectContent><SelectItem value="none">Office to assign</SelectItem>{config?.people.map(p => <SelectItem key={p.id} value={p.id}>{p.name || "Team member"}</SelectItem>)}</SelectContent></Select></div><div><Label htmlFor="follow-up-due">Due date (optional)</Label><Input id="follow-up-due" type="date" value={form.follow_up_due_date} onChange={set("follow_up_due_date")} /></div></div><p className="text-xs text-muted-foreground">{config?.can_assign ? "An action and person create an assigned corrective action. Otherwise the office can complete follow-up later." : "Your suggestion is saved for office review; the office confirms action assignments."}</p>{!config && <ConfigNotice error={configError} onRetry={loadConfig} />}</div>}
        </section>}
        {isIncident && <section className="space-y-2"><label className="flex items-start gap-3 cursor-pointer"><Checkbox checked={form.notifiable} onCheckedChange={v => setForm(f => ({ ...f, notifiable: Boolean(v) }))} data-testid="safety-notifiable-checkbox" /><span className="text-sm font-semibold">Potential notifiable event</span></label><p className="text-sm text-muted-foreground">Flag for urgent supervisor review. This report does not determine legal notifiability; use your organisation’s notification process.</p></section>}
                {/* NZ HSWA / WorkSafe-aligned incident details — only for incidents */}
        {form.form_type === "incident" && (
          <details className="border border-zinc-300 rounded-xl p-4 space-y-3" data-testid="safety-hswa-section">
            <summary className="font-bold cursor-pointer min-h-11">Additional incident details (optional)</summary>
            <div className="flex items-center justify-between">
              <div className="label-tag flex items-center gap-1.5"><span className="text-[#FF5F15]">/</span> NZ HSWA incident details</div>
              <div className="text-[10px] text-zinc-500 font-mono">Health &amp; Safety at Work Act 2015</div>
            </div>
            <p className="text-[11px] text-zinc-500 italic">Fill in what you know — leave the rest blank. The office can complete this later for the WorkSafe record.</p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="label-tag">Event type</Label>
                <Select value={form.event_type} onValueChange={set("event_type")}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="safety-event-type"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="injury">Injury</SelectItem>
                    <SelectItem value="illness">Illness</SelectItem>
                    <SelectItem value="near_miss">Near miss</SelectItem>
                    <SelectItem value="property_damage">Property damage</SelectItem>
                    <SelectItem value="environmental">Environmental</SelectItem>
                    <SelectItem value="dangerous_occurrence">Dangerous occurrence</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tag">Person harmed — name</Label>
                <Input value={form.person_harmed_name} onChange={set("person_harmed_name")} className="mt-1.5 rounded-sm border-zinc-400" placeholder="e.g. Sam Te Whata" data-testid="safety-person-name" />
              </div>
              <div>
                <Label className="label-tag">Person harmed — role</Label>
                <Select value={form.person_harmed_role} onValueChange={set("person_harmed_role")}>
                  <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="safety-person-role"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="subcontractor">Subcontractor</SelectItem>
                    <SelectItem value="visitor">Visitor</SelectItem>
                    <SelectItem value="member_of_public">Member of public</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-tag">Body part(s) affected</Label>
                <Input value={form.body_parts} onChange={set("body_parts")} className="mt-1.5 rounded-sm border-zinc-400" placeholder="left hand, lower back" data-testid="safety-body-parts" />
              </div>
              <div>
                <Label className="label-tag">Injury type</Label>
                <Input value={form.injury_type} onChange={set("injury_type")} className="mt-1.5 rounded-sm border-zinc-400" placeholder="laceration, fracture, burn..." data-testid="safety-injury-type" />
              </div>
              <div>
                <Label className="label-tag">Lost time (days off work)</Label>
                <Input type="number" min="0" step="0.5" value={form.lost_time_days} onChange={set("lost_time_days")} className="mt-1.5 rounded-sm border-zinc-400 font-mono" placeholder="0" data-testid="safety-lost-time" />
              </div>
            </div>

            <div>
              <Label className="label-tag">Plant / substance involved</Label>
              <Input value={form.plant_substance_involved} onChange={set("plant_substance_involved")} className="mt-1.5 rounded-sm border-zinc-400" placeholder="e.g. 5T excavator, concrete saw, diesel" data-testid="safety-plant" />
            </div>
            <div>
              <Label className="label-tag">Witnesses</Label>
              <Textarea rows={2} value={form.witnesses} onChange={set("witnesses")} className="mt-1.5 rounded-sm border-zinc-400 text-sm" placeholder="Names + roles of anyone who saw it" data-testid="safety-witnesses" />
            </div>
            <div>
              <Label className="label-tag">Contributing factors</Label>
              <Textarea rows={2} value={form.contributing_factors} onChange={set("contributing_factors")} className="mt-1.5 rounded-sm border-zinc-400 text-sm" placeholder="Weather, fatigue, equipment fault, rushed timeline..." data-testid="safety-factors" />
            </div>
            <div>
              <Label className="label-tag">Root cause (office can fill later)</Label>
              <Textarea rows={2} value={form.root_cause} onChange={set("root_cause")} className="mt-1.5 rounded-sm border-zinc-400 text-sm" placeholder="The underlying 'why this happened' after investigation" data-testid="safety-root-cause" />
            </div>
            <div>
              <Label className="label-tag">Corrective actions</Label>
              <Textarea rows={2} value={form.corrective_actions} onChange={set("corrective_actions")} className="mt-1.5 rounded-sm border-zinc-400 text-sm" placeholder="What we did and what we'll change to prevent recurrence" data-testid="safety-corrective" />
            </div>

            {form.notifiable && (
              <div className="border-l-4 border-red-600 bg-red-50 p-3 space-y-2" data-testid="safety-worksafe-block">
                <div className="font-bold text-sm text-red-900 uppercase tracking-wider">WorkSafe NZ notification</div>
                <p className="text-[11px] text-red-800">Record any notification made through your organisation’s incident response process. A flag here is not a legal determination.</p>
                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider cursor-pointer">
                  <Checkbox checked={form.worksafe_notified} onCheckedChange={(v) => setForm({ ...form, worksafe_notified: Boolean(v) })} data-testid="safety-ws-notified" className="rounded-sm border-red-500 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600" />
                  WorkSafe has been notified
                </label>
                {form.worksafe_notified && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="label-tag">Notified at</Label>
                      <Input type="datetime-local" value={form.worksafe_notified_at} onChange={set("worksafe_notified_at")} className="mt-1.5 rounded-sm border-red-300" data-testid="safety-ws-when" />
                    </div>
                    <div>
                      <Label className="label-tag">WorkSafe reference #</Label>
                      <Input value={form.worksafe_reference} onChange={set("worksafe_reference")} className="mt-1.5 rounded-sm border-red-300 font-mono" placeholder="e.g. WS-123456" data-testid="safety-ws-ref" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </details>
        )}


        </fieldset>
        <DialogFooter className="safety-report-footer"><p className="text-xs text-muted-foreground mr-auto">{isHazard ? "Report now. Follow-up stays with the office." : "Report details can be reviewed by the office."}</p><Button type="submit" disabled={busy || uploading || (assessed && !config)} className="bg-[#FF5F15] hover:bg-[#E65100] text-white min-h-11" data-testid="safety-submit-button">{uploading ? "Uploading photos…" : busy ? "Submitting…" : isHazard ? "Submit hazard" : "Submit report"}</Button></DialogFooter>
      </form>
    </DialogContent>
  );
}


function HubTile({ to, icon: Icon, title, desc, testId, highlight }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group relative p-3 border-2 transition-all flex items-center gap-3 ${
        highlight
          ? "bg-[#09090B] text-white border-[#09090B] hover:bg-zinc-900 shadow-[4px_4px_0_0_#FF5F15]"
          : "bg-white dark:bg-zinc-900 border-zinc-300 dark:border-white/15 hover:border-[#FF5F15]"
      }`}
    >
      <div className={`w-10 h-10 shrink-0 flex items-center justify-center ${
        highlight ? "bg-[#FF5F15]" : "bg-zinc-100 group-hover:bg-[#FF5F15] group-hover:text-white"
      } transition-colors`}>
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold uppercase tracking-tight text-sm break-words">{title}</div>
        <div className={`text-[11px] mt-0.5 leading-tight ${highlight ? "text-zinc-400" : "text-zinc-500"}`}>{desc}</div>
      </div>
      <ChevronRight className={`w-4 h-4 shrink-0 ${highlight ? "text-zinc-500" : "text-zinc-400 group-hover:text-[#FF5F15]"}`} />
    </Link>
  );
}

function ConfigNotice({ error, onRetry }) {
  return <p className="text-sm text-muted-foreground" role={error ? "alert" : "status"}>{error ? "Could not load reporting options." : "Loading reporting options…"}{error && <Button type="button" variant="outline" size="sm" className="ml-2" onClick={onRetry}>Retry</Button>}</p>;
}
function ChoiceOptions({ value, onChange, choices, label }) {
  return <div className="flex flex-wrap gap-2 mt-1" role="group" aria-label={label}>{choices.map(([key, title]) => <button type="button" key={key} aria-pressed={value === key} onClick={() => onChange(key)} className={`min-h-11 px-3 py-2 rounded-md border text-sm text-left ${value === key ? "border-[#FF5F15] bg-orange-50 text-orange-950 dark:bg-orange-950 dark:text-orange-100 font-semibold" : "border-border bg-card"}`}>{title}</button>)}</div>;
}
function RiskBadge({ risk, label = "Initial risk" }) {
  if (!risk) return <span className="text-sm text-muted-foreground">{label}: select likelihood and consequence</span>;
  return <span className={`safety-risk safety-risk--${risk.rating}`}>{label}: <strong>{risk.rating.toUpperCase()}</strong><span className="text-xs">{risk.score}/25</span></span>;
}
function RiskAssessment({ config, likelihood, consequence, onChange, prefix, required = false }) {
  const risk = config?.risk?.cells?.[Number(likelihood) - 1]?.[Number(consequence) - 1];
  return <div className="space-y-3"><div className="grid grid-cols-2 gap-3">{[["likelihood", likelihood], ["consequence", consequence]].map(([key, value]) => <div key={key}><Label htmlFor={`${prefix}-${key}`}>{prefix === "residual" ? "Residual " : ""}{key[0].toUpperCase() + key.slice(1)}{required ? " *" : ""}</Label><select id={`${prefix}-${key}`} required={required} disabled={!config} value={value ?? ""} onChange={e => onChange({ [`${prefix}_${key}`]: e.target.value })} className="w-full min-h-11 border border-border rounded-md bg-card text-foreground px-2"><option value="">Select…</option>{config?.risk[key].map((v, i) => <option key={v} value={i + 1}>{v}</option>)}</select></div>)}</div><div role="status"><RiskBadge risk={risk} label={prefix === "residual" ? "Residual risk" : "Initial risk"} /></div>{config && <details className="text-xs text-muted-foreground"><summary className="cursor-pointer py-2">How risk is calculated</summary><p>{config.risk.description}</p></details>}</div>;
}

function ReportDetails({ record: s, config }) {
  const category = config?.categories.find(([key]) => key === s.hazard_category)?.[1] || s.hazard_category?.replaceAll("_", " ");
  return <div className="space-y-3">
    <DetailGrid>
      <DetailItem label="Hazard / near-miss category" value={category} />
      <DetailItem label="Anyone injured?" value={s.anyone_injured} />
      <DetailItem label="What was damaged" value={s.damage_description} />
      <DetailItem label="Safe to operate (reported)" value={s.safe_to_operate} />
      <DetailItem label="Event date/time" value={s.event_datetime ? new Date(s.event_datetime).toLocaleString("en-NZ") : null} />
      {s.initial_risk && <><DetailItem label="Initial likelihood" value={config?.risk.likelihood[s.initial_risk.likelihood - 1] || String(s.initial_risk.likelihood)} /><DetailItem label="Initial consequence" value={config?.risk.consequence[s.initial_risk.consequence - 1] || String(s.initial_risk.consequence)} /></>}
    </DetailGrid>
    {s.initial_risk && <RiskBadge risk={s.initial_risk} />}
    {s.immediate_controls?.length > 0 && <div><h3 className="font-semibold text-sm">Immediate controls</h3><ul className="list-disc pl-5 text-sm space-y-1">{s.immediate_controls.map(key => <li key={key}>{config?.controls.find(([k]) => key === k)?.[1] || key.replaceAll("_", " ")}</li>)}</ul></div>}
    {s.further_action_required != null && <p className="text-sm"><strong>Reporter’s resolution: </strong>{s.further_action_required ? "Further action required" : "Controlled / response complete — subject to office review"}</p>}
    {s.further_action_required && !s.follow_up_action_id && <div className="border-l-2 border-[#FF5F15] pl-3 text-sm"><h3 className="font-semibold">Requested follow-up — office to confirm</h3><p>{s.follow_up_action || "Action details to be completed"}</p><p>{s.follow_up_assigned_name ? `Suggested person: ${s.follow_up_assigned_name}` : "Person to be assigned"}{s.follow_up_due_date ? ` · Requested due date: ${s.follow_up_due_date}` : ""}</p></div>}
    {s.residual_risk && <RiskBadge risk={s.residual_risk} label="Residual risk" />}
    {s.verification_notes && <p className="text-sm whitespace-pre-wrap"><strong>Verification: </strong>{s.verification_notes}</p>}
    {s.verified_at && <p className="text-sm text-muted-foreground">Verified by {s.verified_by_name} · {new Date(s.verified_at).toLocaleString("en-NZ")}</p>}
    {s.closed_at && <p className="text-sm text-muted-foreground">Closed by {s.closed_by} · {new Date(s.closed_at).toLocaleString("en-NZ")}</p>}
    {s.form_type === "incident" && <details><summary className="text-sm font-semibold cursor-pointer min-h-11">Incident details</summary><DetailGrid>{[["event_type", "Event type"], ["person_harmed_name", "Person harmed"], ["person_harmed_role", "Role"], ["injury_type", "Injury"], ["treatment_given", "Treatment"], ["witnesses", "Witnesses"], ["root_cause", "Root cause"], ["contributing_factors", "Contributing factors"], ["corrective_actions", "Recorded corrective action notes"], ["worksafe_reference", "WorkSafe reference"]].map(([key, label]) => <DetailItem key={key} label={label} value={s[key]} />)}</DetailGrid></details>}
  </div>;
}
